import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { useCart } from '../context/CartContext';
import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';

export default function Header() {
  const { getImage } = useSiteImageOverrides();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { totalItems, totalPrice, setCartOpen } = useCart();

  // Handle cart button click - go to order page and open cart
  const handleCartClick = () => {
    if (location.pathname === '/order') {
      setCartOpen(true);
    } else {
      navigate('/order');
      // Small delay to let navigation complete, then open cart
      setTimeout(() => setCartOpen(true), 100);
    }
  };

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Menu', href: '/menu' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'About', href: '/about' },
    { name: 'Guides', href: '/guides' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <nav className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src={getImage('logo', '/assets/images/branding/logo2.webp')} 
              alt="Nanny B's Bakery Logo" 
              className="h-14 w-auto"
            />
            <div className="hidden sm:block">
              <h1 className="text-2xl font-display font-bold text-primary-700">
                Nanny B's Bakery
              </h1>
              <p className="text-xs text-gray-600">Fresh Baked Daily</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Premium Cart Button */}
            <button
              onClick={handleCartClick}
              className="group relative flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 ease-out"
              aria-label="View cart"
              data-testid="header-cart"
            >
              {/* Cart Icon with animation */}
              <span className="relative">
                <ShoppingCartIcon className="h-5 w-5 group-hover:animate-bounce" />
                {totalItems > 0 && (
                  <span className="absolute -top-3 -right-3 bg-amber-400 text-primary-900 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse shadow-md">
                    {totalItems}
                  </span>
                )}
              </span>
              
              {/* Text - shows price when items exist */}
              <span className="font-medium text-sm">
                {totalItems > 0 ? `$${totalPrice.toFixed(2)}` : 'Cart'}
              </span>
              
              {/* Shine effect on hover */}
              <span className="absolute inset-0 rounded-full bg-white/20 opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-300" />
            </button>
          </div>

          {/* Mobile: Cart + Menu button */}
          <div className="md:hidden flex items-center gap-3">
            {/* Mobile Cart Button - Premium Style */}
            <button
              onClick={handleCartClick}
              className="relative p-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-full shadow-md hover:shadow-lg active:scale-95 transition-all duration-200"
              aria-label="View cart"
            >
              <ShoppingCartIcon className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-amber-400 text-primary-900 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse shadow-sm">
                  {totalItems}
                </span>
              )}
            </button>
            <button
              type="button"
              className="p-2 rounded-md text-gray-700 hover:text-primary-600"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-3 py-2 rounded-md text-base font-medium ${
                    isActive(item.href)
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
